import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  /** Optional counter shown after the label. */
  count?: number;
}

/** Compact tab-like switch between a few mutually exclusive options. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
  className,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'inline-flex rounded-lg bg-surface-2 p-0.5 ring-1 ring-inset ring-border',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors [&_svg]:size-4',
              size === 'sm' ? 'h-6 px-2 text-xs' : 'h-7 px-2.5 text-[13px]',
              active ? 'bg-surface text-text shadow-panel' : 'text-muted hover:text-text',
            )}
          >
            {option.icon}
            {option.label}
            {option.count !== undefined && (
              <span className={cn('tabular-nums', active ? 'text-muted' : 'text-subtle')}>
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
